/**
 * Theme context for application theming
 * Light mode only - dark mode has been removed for cleaner UI
 */
import React, { createContext, useContext, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme } from '@core/config/theme.config';

/**
 * Theme mode type - light only
 */
type ThemeMode = 'light';

/**
 * Theme context interface
 * Note: toggleTheme and setTheme are no-ops for backward compatibility
 */
interface ThemeContextType {
    mode: ThemeMode;
    toggleTheme: () => void;
    setTheme: (mode: ThemeMode) => void;
}

/**
 * Theme context
 */
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Theme provider props
 */
interface ThemeContextProviderProps {
    children: React.ReactNode;
}

/**
 * Theme context provider component
 * Always uses light mode - dark mode has been removed
 * @param props - Provider props
 * @returns JSX Element
 */
export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
    // Always light mode
    const mode: ThemeMode = 'light';

    /**
     * No-op toggle function (kept for backward compatibility)
     */
    const toggleTheme = (): void => {
        // Dark mode removed - no-op
    };

    /**
     * No-op set theme function (kept for backward compatibility)
     */
    const setTheme = (_newMode: ThemeMode): void => {
        // Dark mode removed - no-op
    };

    /**
     * Creates theme using existing theme configuration
     */
    const theme = useMemo(() => createAppTheme(), []);

    const value = useMemo(
        () => ({
            mode,
            toggleTheme,
            setTheme,
        }),
        []
    );

    return (
        <ThemeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ThemeContext.Provider>
    );
};

/**
 * Custom hook to use theme context
 * @returns Theme context value
 */
export const useThemeContext = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useThemeContext must be used within a ThemeContextProvider');
    }
    return context;
};
